/**
 * Payment Element tab order for the checkout surfaces that offer instalments.
 *
 * Why this exists: with instalments on but no explicit order, Stripe decides
 * the tab order itself and on a narrow (mobile) Payment Element it pushed
 * Klarna/Afterpay into the "+ more" overflow behind Cash App Pay: the buyer
 * who is there BECAUSE of instalments could not see them. So whenever the
 * feature is on we state the order; only the lead method differs.
 *
 * ORDERING ONLY. We never pass `paymentMethodTypes`: dynamic payment methods
 * must keep deciding eligibility (an allowlist would render ineligible tabs and
 * hide methods Stripe enables later). Card stays one click away in both orders.
 *
 * Display/UI only; it touches no amount and no currency.
 */
import { normalizeBillingCountry } from "./payment-element-defaults";

/** Instalments-led order: the buyer arrived through an instalments control. */
const INSTALLMENTS_FIRST_ORDER = ["klarna", "afterpay_clearpay", "card"];
/** Default order: card leads, instalments stay visible next to it. */
const CARD_FIRST_ORDER = ["card", "klarna", "afterpay_clearpay"];

/**
 * The `paymentMethodOrder` to pass to the Payment Element, or `undefined` when
 * the key must be omitted entirely.
 *
 * With `installmentsEnabled` false the caller spreads `undefined` into the
 * options object, which is byte-for-byte the options the surface had before
 * instalments existed.
 */
export function paymentMethodOrderFor(
  installmentsEnabled: boolean,
  installmentsFirst: boolean,
): string[] | undefined {
  if (!installmentsEnabled) return undefined;
  return installmentsFirst ? [...INSTALLMENTS_FIRST_ORDER] : [...CARD_FIRST_ORDER];
}

/**
 * `<Elements key=...>` for a one-time PaymentIntent tree, derived from the tab
 * order that tree will render with.
 *
 * Why the order belongs in the key: Stripe fixes the Payment Element's tab
 * order when the element is CREATED. react-stripe-js maps a later options
 * change onto `element.update({ paymentMethodOrder })` and Stripe does not
 * re-sort the tabs for it, so an order that is only known after the first
 * render is silently dropped and the buyer keeps Stripe's own ordering (Klarna
 * pushed into the "+ more" overflow). Both inputs resolve asynchronously on the
 * surfaces that use this: the island's `installmentsFirst` arrives on a window
 * event from the landing page's instalments control, and the standalone
 * checkout reads its sessionStorage handoff in a mount effect. Keying on the
 * order remounts the tree once instead, so the element is CREATED with it.
 *
 * The buyer's `country` is part of the key for the same reason: it is seeded
 * into the element at creation (see payment-element-defaults.ts) and a country
 * that only arrives with the geo response would otherwise reach a Payment
 * Element that has already decided what to render. It is included only while
 * an order is present, and only once it is a usable code, so the key changes
 * at most once per checkout.
 *
 * With instalments off `order` is `undefined` and the key is the client secret
 * verbatim - byte-for-byte the key the surface had before instalments existed.
 */
export function elementsKeyFor(
  clientSecret: string,
  order: string[] | undefined,
  country?: string | null,
): string {
  if (!order) return clientSecret;
  const base = `${clientSecret}:${order.join("-")}`;
  const code = normalizeBillingCountry(country);
  return code ? `${base}:${code}` : base;
}
